/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomLogsDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Slide from "@mui/material/Slide";
import CloseIcon from "@mui/icons-material/Close";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import customConsole from "../../config/customConsole";
import { shallowEqual, useSelector } from "react-redux";
import CustomPaginationV2 from "../customComponentsV2/common/pagination/CustomPaginationV2";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

function CustomLogsDialog({
  open,
  handleClose,
  dialogTitle,
  dialogContent,
  data,
  getLogsData,
}) {
  const { rolesList } = useSelector(
    (state) => state.userManagementReducer,
    shallowEqual
  );
  const profileDetails = useSelector((store) => store.profileReducer);

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [details, setDetails] = useState("");
  const [logsList, setLogsList] = useState([]);
  const [userRolesList, setUserRolesList] = useState(null);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    const userRolesObject = rolesList.reduce((acc, el) => {
      const roleId = Number.parseInt(el?.role_id, 10);
      if (!Number.isNaN(roleId)) {
        acc[roleId] = {
          role_name: el?.role_name,
          role_department: el?.department,
        };
      }
      return acc;
    }, {});

    setUserRolesList(userRolesObject);
  }, [rolesList]);

  useEffect(() => {
    customConsole("*------- Get Roles List -------*");
    customConsole({ userRolesList });
  }, [userRolesList]);

  useEffect(() => {
    setLogsList(dialogContent || []);
  }, [dialogContent]);

  useEffect(() => {
    setDetails(data || {});
  }, [data]);

  useEffect(() => {
    if (open) {
      customConsole("*------- Get Change Logs -------*");
      customConsole({
        ee_id: Number(details?.ee_id),
        swm_id: Number(details?.swm_id),
        user_id: details?.user_id,
        pageNo: page,
        pageSize: rowsPerPage,
      });

      getLogsData({
        ee_id: Number(details?.ee_id),
        swm_id: Number(details?.swm_id),
        user_id: details?.user_id,
        pageNo: page,
        pageSize: rowsPerPage,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, page, rowsPerPage, open]);

  return (
    <BootstrapDialog
      aria-labelledby="customized-dialog-title"
      open={open}
      TransitionComponent={Transition}
      maxWidth={"md"}
      fullWidth
    >
      <DialogTitle sx={styles.dialogTitle} id="customized-dialog-title">
        <Box
          sx={{
            pt: 0.4,
            pl: 2,
            pr: 0.4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
            {dialogTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              ...styles.closeIconButton,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={styles.dialogContent}>
        {logsList?.length ? (
          <Box sx={{ width: "100%" }}>
            <Box sx={styles.tableHeader}>
              <Typography sx={{ ...styles.headerCell, flex: 2 }}>
                Changes
              </Typography>
              <Typography sx={{ ...styles.headerCell, flex: 2 }}>
                Changed by
              </Typography>
              <Typography sx={{ ...styles.headerCell, flex: 1.5 }}>
                Time stamp
              </Typography>
            </Box>

            {logsList?.map((el, index) => (
              <Box
                key={el?.id || el?.log_id || `${el?.created_at}-${index}`}
                sx={{
                  ...styles.tableRow,
                  borderBottom:
                    index === logsList.length - 1
                      ? "none"
                      : "1px solid #E5E7EB",
                }}
              >
                <Typography sx={{ ...styles.tableCell, flex: 2 }}>
                  {JSON.stringify(el?.current_changes)}
                </Typography>
                <Typography sx={{ ...styles.tableCell, flex: 2 }}>
                  {el?.changed_by?.name},{el?.changed_by?.email}
                </Typography>
                <Typography sx={{ ...styles.tableCell, flex: 1.5 }}>
                  {convertUtcToTimeZone(
                    el?.created_at,
                    profileDetails?.profile?.profile?.region
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={styles.noLogsFoundContainer}>
            <Typography>No logs found</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>

        <CustomPaginationV2
          size="small"
          totalItems={10}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
        />
      </DialogActions>
    </BootstrapDialog>
  );
}

CustomLogsDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  dialogTitle: PropTypes.node,
  dialogContent: PropTypes.array,
  data: PropTypes.object,
  getLogsData: PropTypes.func,
};

export default CustomLogsDialog;

const styles = {
  dialogTitle: {
    m: 0,
    px: 0,
    pt: 0.4,
    borderBottom: "1px solid #E5E7EB",
  },
  closeIconButton: {},
  dialogContent: {
    minHeight: "300px",
    padding: 0,
  },
  noLogsFoundContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  dialogActions: {
    boxShadow: 4,
  },
  tableHeader: {
    display: "flex",
    backgroundColor: "#F9FAFB",
    borderBottom: "2px solid #E5E7EB",
    padding: "12px 16px",
  },
  headerCell: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#111827",
    textAlign: "left",
  },
  tableRow: {
    display: "flex",
    padding: "12px 16px",
    alignItems: "flex-start",
  },
  tableCell: {
    fontSize: "14px",
    color: "#374151",
    textAlign: "left",
    wordBreak: "break-word",
    paddingRight: "8px",
  },
};
